import React, { ReactNode } from 'react';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { Field, FieldArray, WrappedFieldArrayProps } from 'redux-form';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import TextFormField from '../../../../shared/forms/fields/TextFormField';
import SelectFormField from '../../../../shared/forms/fields/SelectFormField';
import { SmscMode } from '../../../../models/smscModel';
import { parseNumberFieldValue } from '../../../../shared/forms/utils/parseNumberFieldValue';

export const DEFAULT_SMSC_CONNECTION_CONFIG = {
  mode: 'TRANSCEIVER',
  sourceAddrNpi: 1,
  sourceAddrTon: 1,
  destAddrNpi: 1,
  destAddrTon: 1,
  systemType: '',
  enquireLinkInterval: 60,
  throughput: 20,
  instances: 5,
};

type ConnectionProps = {
  readonly fields: Object[],
  readonly meta: { error: ReactNode, submitFailed: Boolean },
  readonly readOnly: boolean,
};

const renderConnections = ({
  fields,
  meta: { error, submitFailed },
  readOnly,
}: WrappedFieldArrayProps<any> & ConnectionProps) => {
  const smscModeOptions = [
    { label: 'TRANSCEIVER', value: SmscMode.TRANSCEIVER },
    { label: 'RECEIVER', value: SmscMode.RECEIVER },
    { label: '', value: SmscMode.TRANSMITTER },
  ];

  return (
    <div>
      {!readOnly && (
        <>
          <Button size='small' onClick={() => fields.push(DEFAULT_SMSC_CONNECTION_CONFIG)}>
            Add connection
          </Button>
          <Divider/>
        </>
      )}
      {submitFailed && error && <span>{error}</span>}

      {fields.map((conn, index) => (
        <Paper key={index} style={{padding: '15px', marginBottom: '10px'}}>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Field
                name={`${conn}.username`}
                disabled={readOnly}
                type='text'
                fullWidth
                component={TextFormField}
                label='Username'
                required
              />
            </Grid>
            <Grid item xs={4}>
              <Field
                fullWidth
                disabled={readOnly}
                label='Password'
                name={`${conn}.password`}
                component={TextFormField}
                required
              />
            </Grid>
            <Grid item xs={4} display='flex' justifyContent='flex-end'>
              <IconButton onClick={() => fields.remove(index)} disabled={readOnly}>
                <DeleteForeverIcon/>
              </IconButton>
            </Grid>

            <Grid item xs={4}>
              <Field
                name={`${conn}.host`}
                type='text'
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Host'
                required
              />
            </Grid>
            <Grid item xs={4}>
              <Field
                format={(value: String) => value || []}
                fullWidth
                type='number'
                min={0}
                disabled={readOnly}
                label='Port'
                name={`${conn}.port`}
                component={TextFormField}
                required
                parse={parseNumberFieldValue}
              />
            </Grid>
            <Grid item xs={4}>
              <Field
                format={(value: String) => value || []}
                fullWidth
                disabled={readOnly}
                label='Mode'
                name={`${conn}.mode`}
                component={SelectFormField}
                required
              >
                { smscModeOptions.map(({label, value}) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Field>
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.sourceAddrNpi`}
                type='number'
                min={0}
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Source NPI'
                parse={parseNumberFieldValue}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.sourceAddrTon`}
                type='number'
                min={0}
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Source TON'
                parse={parseNumberFieldValue}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.destAddrNpi`}
                type='number'
                min={0}
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Destination NPI'
                parse={parseNumberFieldValue}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.destAddrTon`}
                type='number'
                min={0}
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Destination TON'
                parse={parseNumberFieldValue}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.systemType`}
                type='text'
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='System type'
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.enquireLinkInterval`}
                type='number'
                min={0}
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Enquire link interval'
                parse={parseNumberFieldValue}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.throughput`}
                type='number'
                min={0}
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Throughput'
                parse={parseNumberFieldValue}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.instances`}
                type='number'
                min={0}
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Instances'
                parse={parseNumberFieldValue}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.throughputMt`}
                type='number'
                min={0}
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Throughput MT'
                parse={parseNumberFieldValue}
              />
            </Grid>
            <Grid item xs={3}>
              <Field
                name={`${conn}.throughputMo`}
                type='number'
                min={0}
                fullWidth
                disabled={readOnly}
                component={TextFormField}
                label='Throughput MO'
                parse={parseNumberFieldValue}
              />
            </Grid>
          </Grid>
        </Paper>
      ))}
    </div>
  );
};

type Props = {
  readonly readOnly: boolean;
}

const SmscConnectionsFields = (props: Props) => {
  const { readOnly } = props;

  return (
    <Grid item xs={12} style={{ marginTop: 20 }}>
      <FieldArray
        name='connections'
        component={renderConnections}
        readOnly={readOnly}
      />
    </Grid>
  )
};

export default SmscConnectionsFields;
